/* Build in libraries */
import { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';

/* Third party libraries */
import { XIcon } from '@heroicons/react/outline';
import { LocationMarkerIcon } from '@heroicons/react/solid';
import { useQueries } from '@tanstack/react-query';

/* Components */
import TotalApplicationsWidget from 'components/Rover/Widgets/TotalApplicationsWidget';
import Card from 'components/UI/Card';
import PieChart from 'components/UI/Charts/PieChart';
import { CRApprovedDashboard } from './CRApprovedDashboard';
import ToBeInspectedWidget from 'components/Rover/Widgets/ToBeInspectedWidget';
import TotalDeniedRulesWidget from 'components/Rover/Widgets/TotalDeniedRulesWidget';
import { PhoneNumberFormatDisplay } from 'components/UI/NumberFormatDisplay/PhoneNumberFormatDisplay';
import LocationMarker from 'components/UI/GoogleMap/LocationMarker';
import GoogleMap from 'components/UI/GoogleMap';
import DistrictBreakdownDeniedWidget from 'components/Rover/Widgets/DistrictBreakdownDeniedWidget';

/* Services */
import { EApplicationStatus } from 'features/ApplicationForm/constants';
import { convertEnumToText, isObjectEmpty } from 'utils';
import { IGetAddressDecodeResData, getAddressDecode } from 'api/misc';
import { gender, races } from '../CRForm/ApplicationInformation/constants';

/* Assets */
export const convertIntoPercentage = (
  arrayOfData: { data: number; color: string }[]
) => {
  const totalData = arrayOfData.reduce(
    (acc: number, currentData: { data: number; color: string }) => {
      return (acc += currentData.data);
    },
    0
  );
  const percentage = arrayOfData?.map((d: { data: number; color: string }) => {
    const convertNumberIntoPercentage = (d.data * 100) / totalData;
    const convertFloatToInteger = Math.ceil(convertNumberIntoPercentage);
    return {
      ...d,
      data: convertFloatToInteger ? convertFloatToInteger : 0,
    };
  });

  return percentage;
};

interface IApplicationDashboardComponentProps {
  data: any;
}

interface IPieChart {
  label: string[];
  dataSets: { data: number; color: string }[];
}

export const CRDashboard = ({ data }: IApplicationDashboardComponentProps) => {
  const location = useLocation();
  const [selectedProject, setSelectedProject] = useState<any>();
  const [viewStatus, setViewStatus] = useState<string>('ALL');
  const applicationsList = data?.data;

  const widgetsData: {
    totalApplications: number;
    totalHousePrice: number;
    statusSummary: { [key: string]: number };
    gender: IPieChart;
    totalToBeInspected: number;
    totalReceivedAssistance: number;
    maritalStatus: IPieChart;
    totalRaces: IPieChart;
    totalDisabled: IPieChart;
    totalFemaleHeadOfHouseHold: IPieChart;
    totalIncomeBreakDown: IPieChart;
    totalHouseBuiltBefore1978: IPieChart;
    applicationLocationList: Array<{
      id: string;
      [key: string]: string;
      fullAddress: string;
    }>;
    districtBreakdownDenied: { [key: string]: number };
  } = {
    totalApplications: 0,
    totalHousePrice: 0,
    statusSummary: {},
    totalRaces: {
      label: races,
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#0FCA98' },
        { data: 0, color: '#2B6CB0' },
        { data: 0, color: '#F27141' },
      ],
    },
    totalDisabled: {
      label: ['Yes', 'No'],
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#0FCA98' },
      ],
    },
    totalFemaleHeadOfHouseHold: {
      label: ['Yes', 'No'],
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#0FCA98' },
      ],
    },
    totalIncomeBreakDown: {
      label: gender,
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#0FCA98' },
      ],
    },
    totalHouseBuiltBefore1978: {
      label: ['Yes', 'No'],
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#0FCA98' },
      ],
    },
    gender: {
      label: ['Female', 'Male'],
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#0FCA98' },
      ],
    },
    totalToBeInspected: 0,
    totalReceivedAssistance: 0,
    maritalStatus: {
      label: ['Married', 'Unmarried', 'Widowed'],
      dataSets: [
        { data: 0, color: '#0FCA98' },
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#2B6CB0' },
      ],
    },
    applicationLocationList: [],
    districtBreakdownDenied: {},
  };

  applicationsList?.forEach((a: any) => {
    const { applicationData, id, contactEmail, contactName } = a;

    // Location
    const applicationLocation = `${applicationData.application.streetAddress}, ${applicationData.application.city}, ${applicationData.application.state}, ${applicationData.application.zipCode}`;
    widgetsData['applicationLocationList'].push({
      id: id,
      email: contactEmail,
      name: contactName,
      phone: applicationData?.application?.phone,
      fullAddress: applicationLocation,
    });
    //Total Application Widget
    widgetsData['totalApplications'] += 1;

    // Total Amount Requested
    widgetsData['totalHousePrice'] += applicationData?.propertyInformation
      ?.housePrice
      ? applicationData?.propertyInformation?.housePrice
      : 0;

    //Status Summary
    widgetsData['statusSummary'][a.status] =
      (widgetsData['statusSummary'][a.status] ?? 0) + 1;

    // Total Races
    applicationData?.application?.race.forEach((r: any) => {
      if (r === 'African-American')
        widgetsData.totalRaces.dataSets[0].data += 1;
      if (r === 'Caucasian') widgetsData.totalRaces.dataSets[1].data += 1;
      if (r === 'Hispanic') widgetsData.totalRaces.dataSets[2].data += 1;
      if (r === 'Other') widgetsData.totalRaces.dataSets[3].data += 1;
    });

    // Total Disabled
    if (applicationData?.application?.disabled === 'yes') {
      widgetsData.totalDisabled.dataSets[0].data += 1;
    }
    if (applicationData?.application?.disabled === 'no') {
      widgetsData.totalDisabled.dataSets[1].data += 1;
    }

    // Total Female Head of Household
    if (applicationData?.application?.femaleHeadOfHousehold === 'yes') {
      widgetsData.totalFemaleHeadOfHouseHold.dataSets[0].data += 1;
    }
    if (applicationData?.application?.femaleHeadOfHousehold === 'no') {
      widgetsData.totalFemaleHeadOfHouseHold.dataSets[1].data += 1;
    }

    // Total House Built Before 1978
    if (applicationData?.application?.wasBuiltBefore1978 === 'yes') {
      widgetsData.totalHouseBuiltBefore1978.dataSets[0].data += 1;
    }
    if (applicationData?.application?.wasBuiltBefore1978 === 'no') {
      widgetsData.totalHouseBuiltBefore1978.dataSets[1].data += 1;
    }

    // gender
    if (applicationData?.application?.gender === 'Female') {
      widgetsData.gender.dataSets[0].data += 1;
    }
    if (applicationData?.application?.gender === 'Male') {
      widgetsData.gender.dataSets[1].data += 1;
    }

    // marital status
    if (applicationData?.application?.maritalStatus === 'Married') {
      widgetsData.maritalStatus.dataSets[0].data += 1;
    }
    if (applicationData?.application?.maritalStatus === 'Un-Married') {
      widgetsData.maritalStatus.dataSets[1].data += 1;
    }
    if (applicationData?.application?.maritalStatus === 'Widow') {
      widgetsData.maritalStatus.dataSets[2].data += 1;
    }

    // Total To Be Inspected
    if (a.status === 'PHYSICAL_INSPECTION') {
      widgetsData.totalToBeInspected += 1;
    }

    // Total Denied - 3 Year Rule
    if (applicationData?.application?.receivedAssistance === 'yes')
      widgetsData.totalReceivedAssistance += 1;

    if (a.status === 'DENIED') {
      //District Breakdown Denied
      const { neighborhood } = applicationData?.application;
      widgetsData['districtBreakdownDenied'][neighborhood] =
        (widgetsData['districtBreakdownDenied'][neighborhood] ?? 0) + 1;
    }
  });

  const fetchApplicationLocation = useQueries({
    queries: widgetsData?.applicationLocationList?.map(
      (d: { id: string; [key: string]: string; fullAddress: string }) => {
        return {
          queryKey: ['GET_APPLICATION_WIDGET_GEO_CODE', d?.id],
          queryFn: () => getAddressDecode(d.fullAddress),
          select: (resData: { data: IGetAddressDecodeResData }) => {
            const { lat, lng } = resData.data;
            return { ...d, coordinates: { lat, lng } };
          },
        };
      }
    ),
  });
  const applicationsGeoLocated = fetchApplicationLocation?.map((d) => d.data);

  const transformDistrict = (districts: { [key: string]: number }) => {
    return Object.entries(districts).map(([name, projects]) => ({
      name,
      projects,
    }));
  };

  return (
    <>
      <div className='max-w-screen-3xl mx-auto'>
        <div className='font-semibold text-bold py-4 rounded-md space-x-2'>
          <span
            className={`${
              viewStatus === 'ALL'
                ? 'bg-primaryColor-normal'
                : 'bg-neutralColor-light hover:bg-primaryColor-normal'
            } text-white bg-primaryColor-normal px-4 py-2 cursor-pointer`}
            onClick={() => setViewStatus('ALL')}
          >
            All Applications
          </span>
          <span
            className={`${
              viewStatus === 'APPROVED'
                ? 'bg-primaryColor-normal'
                : 'bg-neutralColor-light hover:bg-primaryColor-normal'
            } text-white bg-primaryColor-normal px-4 py-2 cursor-pointer`}
            onClick={() => setViewStatus('APPROVED')}
          >
            Approved Applications
          </span>
        </div>
        {viewStatus === 'ALL' && (
          <>
            <div className='my-4'>
              <section className='my-4 grid md:grid-cols-2 xl:grid-cols-4 gap-4'>
                <TotalApplicationsWidget
                  count={widgetsData?.totalApplications ?? 0}
                />
                <ToBeInspectedWidget
                  count={widgetsData?.totalToBeInspected ?? 0}
                />
                <TotalDeniedRulesWidget
                  count={widgetsData?.totalReceivedAssistance ?? 0}
                />
              </section>
              <Card title='Status Summary'>
                <>
                  <div className='grid grid-cols-8 gap-6 py-6 px-6'>
                    {!isObjectEmpty(EApplicationStatus) &&
                      Object.keys(EApplicationStatus).map((s: any) => (
                        <div className='col-span-2 font-medium flex justify-between gap-6'>
                          <span>
                            {convertEnumToText(
                              EApplicationStatus[
                                s as keyof typeof EApplicationStatus
                              ]
                            )}
                          </span>
                          <span>{widgetsData['statusSummary'][s] ?? 0}</span>
                        </div>
                      ))}
                  </div>
                </>
              </Card>
            </div>
            <div className='my-4'>
              <Card title='Project Location'>
                {applicationsGeoLocated.length === 0 && (
                  <div className='w-full flex justify-center items-center h-screen/2'>
                    <div className='flex justify-center items-center gap-2 text-primaryColor-normal'>
                      <LocationMarkerIcon className='h-8 w-8' />
                      <h1>No Projects</h1>
                    </div>
                    <p>There are no assigned projects to display.</p>
                  </div>
                )}
                {applicationsGeoLocated.length > 0 && (
                  <div className='w-full grid grid-cols-6 h-screen/2'>
                    <div className='col-span-6 w-full border-l p-5'>
                      {applicationsGeoLocated[0] && (
                        <GoogleMap
                          defaultCenter={{
                            lat: applicationsGeoLocated[0].coordinates.lat,
                            lng: applicationsGeoLocated[0].coordinates.lng,
                          }}
                        >
                          {applicationsGeoLocated?.map((res: any) => {
                            return (
                              <LocationMarker
                                key={res?.id}
                                lat={res?.coordinates.lat}
                                lng={res?.coordinates.lng}
                              >
                                <div className='relative'>
                                  {selectedProject &&
                                    selectedProject?.id === res?.id && (
                                      <div
                                        onClick={() =>
                                          setSelectedProject(undefined)
                                        }
                                        className='bg-white border rounded absolute p-3 bottom-4 left-5 w-56'
                                      >
                                        <XIcon className='h-4 w-4 absolute right-1 top-1 text-neutralColor-normal hover:text-red-500' />
                                        <Link
                                          to={`${location.pathname.replace(
                                            'dashboard',
                                            'applications'
                                          )}/${
                                            selectedProject?.id
                                          }/application_details`}
                                        >
                                          <p className='font-semibold text-primaryColor-normal underline'>
                                            {selectedProject?.name}
                                          </p>
                                        </Link>
                                        <p className='text-xs'>
                                          {selectedProject?.fullAddress}
                                        </p>
                                        <div className='mt-2 text-sm flex justify-between gap-2'>
                                          <span>Email</span>
                                          <span className='font-semibold'>
                                            {selectedProject?.email}
                                          </span>
                                        </div>
                                        <div className='mt-2 text-sm flex justify-between gap-2'>
                                          <span>Phone</span>
                                          <span className='font-semibold'>
                                            <PhoneNumberFormatDisplay
                                              value={
                                                selectedProject?.phone || 'none'
                                              }
                                            />
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                  {res && (
                                    <LocationMarkerIcon
                                      onClick={() => setSelectedProject(res)}
                                      className={
                                        selectedProject?.id === res?.id
                                          ? 'h-8 w-8 text-black'
                                          : 'h-8 w-8 text-red-500 '
                                      }
                                    />
                                  )}
                                </div>
                              </LocationMarker>
                            );
                          })}
                        </GoogleMap>
                      )}
                    </div>
                  </div>
                )}
              </Card>
            </div>
            <div className='my-4'>
              <section className='grid md:grid-cols-2 xl:grid-cols-2 gap-4 mt-4'>
                <Card title='Marital Status'>
                  <div className='p-5 flex flex-col items-center '>
                    <div className='w-5/6 lg:w-5/5 h-80'>
                      <PieChart
                        labels={widgetsData?.maritalStatus?.label}
                        datasets={convertIntoPercentage(
                          widgetsData?.maritalStatus?.dataSets
                        )}
                        chartTitle=''
                        showValue={true}
                      />
                    </div>
                  </div>
                </Card>
              </section>
              <section className='grid md:grid-cols-2 xl:grid-cols-2 gap-4 mt-4'>
                <Card title='Gender'>
                  <div className='p-5 flex flex-col items-center '>
                    <div className='w-5/6 lg:w-5/5 h-80'>
                      <PieChart
                        labels={widgetsData?.gender?.label}
                        datasets={convertIntoPercentage(
                          widgetsData?.gender?.dataSets
                        )}
                        chartTitle=''
                        showValue={true}
                      />
                    </div>
                  </div>
                </Card>
                <Card title='Race'>
                  <div className='p-5 flex flex-col items-center '>
                    <div className='w-5/6 lg:w-5/5 h-80'>
                      <PieChart
                        labels={widgetsData?.totalRaces?.label}
                        datasets={convertIntoPercentage(
                          widgetsData?.totalRaces?.dataSets
                        )}
                        chartTitle=''
                        showValue={true}
                      />
                    </div>
                  </div>
                </Card>
              </section>
              <section className='grid md:grid-cols-2 xl:grid-cols-2 gap-4 mt-4'>
                <Card title='Disabled'>
                  <div className='p-5 flex flex-col items-center '>
                    <div className='w-5/6 lg:w-5/5 h-80'>
                      <PieChart
                        labels={widgetsData?.totalDisabled?.label}
                        datasets={convertIntoPercentage(
                          widgetsData?.totalDisabled?.dataSets
                        )}
                        chartTitle=''
                        showValue={true}
                      />
                    </div>
                  </div>
                </Card>
                <Card title='Income Breakdown'>
                  <div className='p-5 flex flex-col items-center '>
                    <div className='w-5/6 lg:w-5/5 h-80'>
                      <PieChart
                        labels={widgetsData?.totalRaces?.label}
                        datasets={convertIntoPercentage(
                          widgetsData?.totalRaces?.dataSets
                        )}
                        chartTitle=''
                        showValue={true}
                      />
                    </div>
                  </div>
                </Card>
              </section>
              <section className='grid md:grid-cols-2 xl:grid-cols-2 gap-4 mt-4'>
                <Card title='House Built Before 1978'>
                  <div className='p-5 flex flex-col items-center '>
                    <div className='w-5/6 lg:w-5/5 h-80'>
                      <PieChart
                        labels={widgetsData?.totalHouseBuiltBefore1978?.label}
                        datasets={convertIntoPercentage(
                          widgetsData?.totalHouseBuiltBefore1978?.dataSets
                        )}
                        chartTitle=''
                        showValue={true}
                      />
                    </div>
                  </div>
                </Card>
                <Card title='Female Head of Household'>
                  <div className='p-5 flex flex-col items-center '>
                    <div className='w-5/6 lg:w-5/5 h-80'>
                      <PieChart
                        labels={widgetsData?.totalFemaleHeadOfHouseHold?.label}
                        datasets={convertIntoPercentage(
                          widgetsData?.totalFemaleHeadOfHouseHold?.dataSets
                        )}
                        chartTitle=''
                        showValue={true}
                      />
                    </div>
                  </div>
                </Card>
              </section>
              <section className='grid md:grid-cols-2 xl:grid-cols-2 gap-4 mt-4'>
                <DistrictBreakdownDeniedWidget
                  data={transformDistrict(widgetsData.districtBreakdownDenied)}
                />
              </section>
            </div>
          </>
        )}
        {viewStatus === 'APPROVED' && (
          <CRApprovedDashboard data={data}>
            <Card title='Project Location'>
              {applicationsGeoLocated.length === 0 && (
                <div className='w-full flex justify-center items-center h-screen/2'>
                  <div className='flex justify-center items-center gap-2 text-primaryColor-normal'>
                    <LocationMarkerIcon className='h-8 w-8' />
                    <h1>No Projects</h1>
                  </div>
                  <p>There are no assigned projects to display.</p>
                </div>
              )}
              {applicationsGeoLocated.length > 0 && (
                <div className='w-full grid grid-cols-6 h-screen/2'>
                  <div className='col-span-6 w-full border-l p-5'>
                    {applicationsGeoLocated[0] && (
                      <GoogleMap
                        defaultCenter={{
                          lat: applicationsGeoLocated[0].coordinates.lat,
                          lng: applicationsGeoLocated[0].coordinates.lng,
                        }}
                      >
                        {applicationsGeoLocated?.map((res: any) => {
                          return (
                            <LocationMarker
                              key={res?.id}
                              lat={res?.coordinates.lat}
                              lng={res?.coordinates.lng}
                            >
                              <div className='relative'>
                                {selectedProject &&
                                  selectedProject?.id === res?.id && (
                                    <div
                                      onClick={() =>
                                        setSelectedProject(undefined)
                                      }
                                      className='bg-white border rounded absolute p-3 bottom-4 left-5 w-56'
                                    >
                                      <XIcon className='h-4 w-4 absolute right-1 top-1 text-neutralColor-normal hover:text-red-500' />
                                      <Link
                                        to={`${location.pathname.replace(
                                          'dashboard',
                                          'applications'
                                        )}/${
                                          selectedProject?.id
                                        }/application_details`}
                                      >
                                        <p className='font-semibold text-primaryColor-normal underline'>
                                          {selectedProject?.name}
                                        </p>
                                      </Link>
                                      <p className='text-xs'>
                                        {selectedProject?.fullAddress}
                                      </p>
                                      <div className='mt-2 text-sm flex justify-between gap-2'>
                                        <span>Email</span>
                                        <span className='font-semibold'>
                                          {selectedProject?.email}
                                        </span>
                                      </div>
                                      <div className='mt-2 text-sm flex justify-between gap-2'>
                                        <span>Phone</span>
                                        <span className='font-semibold'>
                                          <PhoneNumberFormatDisplay
                                            value={
                                              selectedProject?.phone || 'none'
                                            }
                                          />
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                {res && (
                                  <LocationMarkerIcon
                                    onClick={() => setSelectedProject(res)}
                                    className={
                                      selectedProject?.id === res?.id
                                        ? 'h-8 w-8 text-black'
                                        : 'h-8 w-8 text-red-500 '
                                    }
                                  />
                                )}
                              </div>
                            </LocationMarker>
                          );
                        })}
                      </GoogleMap>
                    )}
                  </div>
                </div>
              )}
            </Card>
          </CRApprovedDashboard>
        )}
      </div>
    </>
  );
};
